import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardContent } from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Volunteer {
  id: number;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string | null;
  skills: string | null;
  interests: string | null;
  is_active: number;
  created_at: string;
}

interface Activity {
  id: number;
  volunteer_id: number;
  activity_date: string;
  hours: number;
  description: string | null;
}

interface VolunteerDetailProps {
  volunteer: Volunteer;
  onBack: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VolunteerDetail({ volunteer, onBack }: VolunteerDetailProps) {
  const queryClient = useQueryClient();
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [actDate, setActDate] = useState("");
  const [actHours, setActHours] = useState("");
  const [actDesc, setActDesc] = useState("");

  const { data: activities, isLoading } = useQuery<Activity[]>({
    queryKey: ["volunteer-activities", volunteer.id],
    queryFn: async () => {
      const res = await api.get<{ data: Activity[] }>(
        `/volunteers/${volunteer.id}/activities`,
      );
      return res.data;
    },
  });

  const createActivityMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post(`/volunteers/${volunteer.id}/activities`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["volunteer-activities", volunteer.id],
      });
      setShowActivityForm(false);
      setActDate("");
      setActHours("");
      setActDesc("");
    },
  });

  function handleAddActivity(e: React.FormEvent) {
    e.preventDefault();
    createActivityMutation.mutate({
      volunteerId: volunteer.id,
      activityDate: actDate,
      hours: parseFloat(actHours),
      description: actDesc || undefined,
    });
  }

  const totalHours = (activities ?? []).reduce((sum, a) => sum + a.hours, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            {volunteer.first_name} {volunteer.last_name}
          </h2>
          <p className="text-sm text-muted-foreground">Volunteer Detail</p>
        </div>
      </div>

      {/* Info card */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Email</p>
              <p className="text-sm">{volunteer.email ?? "--"}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Phone</p>
              <p className="text-sm">{volunteer.phone ?? "--"}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Status</p>
              <p className="text-sm">
                {volunteer.is_active ? "Active" : "Inactive"}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Skills</p>
              <p className="text-sm">{volunteer.skills ?? "--"}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Interests</p>
              <p className="text-sm">{volunteer.interests ?? "--"}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                Total Hours
              </p>
              <p className="text-sm font-semibold">{totalHours.toFixed(1)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity History */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-foreground">
          Activity History
        </h3>
        <Button
          size="sm"
          className="gap-2"
          onClick={() => setShowActivityForm(!showActivityForm)}
        >
          <Plus className="h-4 w-4" />
          Log Activity
        </Button>
      </div>

      {showActivityForm && (
        <Card>
          <CardContent className="p-4">
            <form onSubmit={handleAddActivity} className="flex flex-wrap items-end gap-3">
              <Input
                label="Date"
                type="date"
                value={actDate}
                onChange={(e) => setActDate(e.target.value)}
                required
                className="w-40"
              />
              <Input
                label="Hours"
                type="number"
                step="0.25"
                min="0"
                value={actHours}
                onChange={(e) => setActHours(e.target.value)}
                required
                className="w-24"
              />
              <Input
                label="Description"
                value={actDesc}
                onChange={(e) => setActDesc(e.target.value)}
                placeholder="What did they do?"
                className="flex-1 min-w-[200px]"
              />
              <Button
                type="submit"
                size="sm"
                loading={createActivityMutation.isPending}
              >
                Add
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowActivityForm(false)}
              >
                Cancel
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="h-32 animate-pulse rounded-lg border bg-muted" />
      )}

      {!isLoading && (activities ?? []).length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Clock className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No activities logged yet.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && (activities ?? []).length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Date
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Hours
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {(activities ?? []).map((activity) => (
                  <tr
                    key={activity.id}
                    className="border-b last:border-0 hover:bg-muted/30"
                  >
                    <td className="px-4 py-3">
                      {new Date(activity.activity_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">{activity.hours}</td>
                    <td className="px-4 py-3">
                      {activity.description ?? "--"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
